import React, { useState, useEffect, useRef } from "react";
import AuthUser from "../../../Hooks/authUser";
import { getUserHook } from "../../../Hooks/getUserHook";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import { server_url } from "../../../Config/API";
import { Icon } from "@iconify/react";

/* ─── Slider card for one lens filter ─────────────────── */
const LensSlider = ({ label, value, savedValue, color, bgClass, shadowClass, onChange }) => {
  const hasChanged = Number(value) !== Number(savedValue);

  return (
    <div
      className={`flex flex-col items-center p-6 rounded-2xl border space-y-5 transition-all duration-300 !bg-black ${
        hasChanged
          ? "border-indigo-200 bg-indigo-50/30 shadow-md shadow-indigo-100"
          : "border-slate-100/70 bg-slate-50/40"
      }`}
    >
      {/* Label row */}
      <div className="flex items-center justify-between w-full">
        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
          {label}
        </span>
        {hasChanged && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            Unsaved
          </span>
        )}
      </div>

      {/* Live preview circle */}
      <div className="relative flex items-center justify-center">
        {/* Outer glow ring — always at full color so the circle is visible */}
        <div
          className={`w-36 h-36 rounded-full transition-all duration-150`}
          style={{
            background: color,
            opacity: Number(value) / 100,   /* ✅ correct: 0–1 */
            // boxShadow: `0 8px 32px ${color}40`,
          }}
        />
        {/* Always-visible ring so user can see the circle even at 0% */}
        {/* <div
          className="absolute inset-0 rounded-full border-2 border-dashed"
          style={{ borderColor: `${color}30` }}
        /> */}
        {/* Value badge */}
        <div
          className="absolute flex flex-col items-center justify-center"
          style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}
        >
          <span className="font-black text-2xl text-white drop-shadow-lg">
            {value}
          </span>
          <span className="text-[10px] font-bold text-white/80">%</span>
        </div>
      </div>

      {/* Gradient progress bar + range input — perfectly stacked */}
      <div className="w-full space-y-2">
        {/* Outer wrapper: visual bar + range input share the same space */}
        <div className="relative w-full" style={{ height: "20px" }}>
          {/* Visual track (background) */}
          <div
            className="absolute inset-y-0 my-auto w-full rounded-full"
            style={{ height: "10px", background: "#e2e8f0" }}
          />
          {/* Filled colored portion */}
          <div
            className="absolute inset-y-0 my-auto left-0 rounded-full transition-all duration-100"
            style={{
              height: "10px",
              width: `${value}%`,
              background: `linear-gradient(90deg, ${color}60, ${color})`,
            }}
          />
          {/* Thumb dot rendered on top */}
          <div
            className="absolute inset-y-0 my-auto w-5 h-5 rounded-full border-2 border-white shadow-md -translate-x-1/2 transition-all duration-100 pointer-events-none"
            style={{
              left: `${value}%`,
              background: color,
              boxShadow: `0 2px 8px ${color}60`,
            }}
          />
          {/* Transparent range input covers the entire bar for interaction */}
          <input
            type="range"
            min="0"
            max="100"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            style={{ margin: 0 }}
          />
        </div>

        {/* Tick labels */}
        <div className="flex justify-between text-[10px] font-bold text-slate-400 px-0.5">
          <span>0%</span>
          <span className="font-black" style={{ color }}>{value}%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Change delta hint */}
      {hasChanged && (
        <p className="text-[10px] font-semibold text-slate-400 text-center">
          Saved value:{" "}
          <span className="font-black text-slate-600">{savedValue}%</span>
          {" "}→ New:{" "}
          <span className="font-black" style={{ color }}>
            {value}%
          </span>
        </p>
      )}
    </div>
  );
};

/* ─── Main component ──────────────────────────────────── */
const UserDashboardSettings = () => {
  const { userInfo } = AuthUser();
  const [user, setUser] = useState({});

  // Live slider values
  const [blue, setBlue] = useState(100);
  const [red, setRed]   = useState(100);

  // Saved (server) values — for showing delta
  const [savedRed,  setSavedRed]  = useState(100);
  const [savedBlue, setSavedBlue] = useState(100);

  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  const hasChanges = Number(red) !== savedRed || Number(blue) !== savedBlue;

  /* Load user data once */
  useEffect(() => {
    getUserHook(userInfo?._id, setUser);
  }, []);

  /* Sync slider state when user object arrives */
  useEffect(() => {
    if (user && Object.keys(user).length) {
      const r = user.redOpacity  !== undefined ? Number(user.redOpacity)  : 100;
      const b = user.blueOpacity !== undefined ? Number(user.blueOpacity) : 100;
      setRed(r);  setSavedRed(r);
      setBlue(b); setSavedBlue(b);
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    await UpdateHooks(
      `${server_url}/user/${userInfo?._id}`,
      { redOpacity: Number(red), blueOpacity: Number(blue) },
      true,
      "Color configuration saved!"
    );
    setSavedRed(Number(red));
    setSavedBlue(Number(blue));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    setRed(savedRed);
    setBlue(savedBlue);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5 select-none my-6">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-indigo-50 flex-shrink-0">
          <Icon icon="solar:palette-bold" className="text-xl text-indigo-600" />
        </div>
        <div>
          <h1 className="text-[15px] font-black text-slate-800 tracking-tight">
            Lens Color Calibration
          </h1>
          <p className="text-[11px] text-slate-400 font-semibold mt-0.5">
            Drag the sliders to set red & blue filter intensity for your 3D glasses
          </p>
        </div>
      </div>

      {/* ── Sliders ── */}
      <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <LensSlider
            label="Red Lens Filter"
            value={red}
            savedValue={savedRed}
            color="#FF0000"
            onChange={setRed}
          />
          <LensSlider
            label="Blue Lens Filter"
            value={blue}
            savedValue={savedBlue}
            color="#001AFF"
            onChange={setBlue}
          />
        </div>

        {/* ── Footer actions ── */}
        <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
          {/* Unsaved warning */}
          <div className="flex-1">
            {hasChanges ? (
              <p className="text-[11px] font-bold text-amber-600 flex items-center gap-1.5">
                <Icon icon="solar:danger-triangle-bold" className="text-base" />
                You have unsaved changes
              </p>
            ) : saved ? (
              <p className="text-[11px] font-bold text-emerald-600 flex items-center gap-1.5">
                <Icon icon="solar:check-circle-bold" className="text-base" />
                Configuration saved successfully
              </p>
            ) : (
              <p className="text-[11px] font-semibold text-slate-400">
                Adjust sliders above, then save
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Reset button — only visible when there are changes */}
            {hasChanges && (
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-1.5 py-2.5 px-4 text-[12px] font-bold text-slate-500 hover:text-slate-800 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all duration-200"
              >
                <Icon icon="solar:refresh-linear" className="text-sm" />
                Reset
              </button>
            )}

            {/* Save button */}
            <button
              type="submit"
              disabled={saving || !hasChanges}
              className={`flex items-center gap-2 py-2.5 px-6 text-[12px] font-extrabold rounded-xl transition-all duration-300 ${
                hasChanges && !saving
                  ? "bg-secondary text-white shadow-md shadow-indigo-500/20 hover:-translate-y-0.5 hover:shadow-lg cursor-pointer"
                  : "bg-slate-100 text-slate-400 cursor-not-allowed"
              }`}
            >
              {saving ? (
                <>
                  <Icon icon="svg-spinners:ring-resize" className="text-sm animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Icon icon="solar:diskette-bold" className="text-sm" />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UserDashboardSettings;
